import { BusinessHoursInput, Calendar, DurationInput, EventApi, EventInput } from '@fullcalendar/core';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import luxonPlugin, { toLuxonDuration } from '@fullcalendar/luxon3';
import datepicker, { DatepickerInstance } from 'js-datepicker';
import { ReviewData } from './reservation-form';

export default class FullCalendar
{
  private readonly doubleClickHandler: EventListener;
  private readonly keyupHandler: EventListener;
  private readonly keydownHandler: EventListener;
  private readonly today: Date;
  private readonly reservationStart: Date;
  private readonly reservationEnd: Date;
  private readonly calendarEl: HTMLElement;
  public calendar: Calendar | null = null;
  private picker: DatepickerInstance | null = null;
  private selection: EventApi|null = null;
  private reviewData: ReviewData | null = null;
  private duration: number = 1;
  private minDuration: number = 1;
  private maxDuration: number = 24;
  private durationPreset: number = 1;
  private businessHours: BusinessHoursInput = [
    {
      daysOfWeek: [ 1, 2, 3, 4, 5, 6, 0], 
      startTime: { hours: 6 },
      endTime: { hours: 23 },
    }
  ];
  private slotMinTime: DurationInput = '06:00:00';
  private slotMaxTime: DurationInput = '23:00:00';
  private events: EventInput[] = [];
  private preselectedEvent: EventApi | null = null;

  constructor()
  {
    let calendarEl = document.getElementById('calendar');
    if(calendarEl instanceof HTMLElement) {
      this.calendarEl = calendarEl;
    } else {
      throw new Error("Calendar element not found");
    }

    this.doubleClickHandler = this.handleDoubleClickEvent.bind(this);
    this.keyupHandler = this.handleKeyupEvent.bind(this);
    this.keydownHandler = this.handleKeydownEvent.bind(this);

    let today = new Date();
    today.setMinutes(0);
    today.setSeconds(0);
    today.setMilliseconds(0);

    let reservationStart = new Date(today);
    reservationStart.setUTCHours(reservationStart.getUTCHours() + 25);

    today.setHours(0);

    let reservationEnd = new Date(today);
    reservationEnd.setFullYear(reservationEnd.getFullYear() + 1);

    this.today = today;
    this.reservationStart = reservationStart;
    this.reservationEnd = reservationEnd;
  }

  public enable(): void
  {
    if(!this.calendar) {
      this.calendarEl.addEventListener('dblclick', this.doubleClickHandler, false);
      document.addEventListener('keyup', this.keyupHandler, false);
      document.addEventListener('keydown', this.keydownHandler, false);
      this.calendar = this.createCalendar();
    }
  }

  public destroy(): void
  {
    if(this.calendar) {
      this.preselectedEvent = this.getSelection();
      this.calendarEl.removeEventListener('dblclick', this.doubleClickHandler);
      document.removeEventListener('keyup', this.keyupHandler);
      document.removeEventListener('keydown', this.keydownHandler);
      this.destroyCalendar();
    }
  }

  private handleDoubleClickEvent(event: MouseEvent): void
  {
    if(
      event.target instanceof HTMLButtonElement
      && event.target.classList.contains('fc-button')
    ) {
      event.preventDefault();
    }
  }
  private handleKeyupEvent(event: KeyboardEvent): void
  {
    if(document.activeElement?.classList.contains('fc-event-draggable'))
    {
      switch (event.key) {
        case '-':
          event.preventDefault();
          this.decrementDuration();
          this.focusOnSelection();
          break;
        case '+':
          event.preventDefault();
          this.incrementDuration();
          this.focusOnSelection();
          break;
        case 'Backspace':
        case 'Delete':
          event.preventDefault();
          this.calendar?.getEventById('selection')?.remove();
          break;
      }
    }
  }
  private handleKeydownEvent(event: KeyboardEvent): void
  {
    if(document.activeElement?.classList.contains('fc-event-draggable')) {
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          this.moveSelection('- 30 minutes');
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.moveSelection('+ 30 minutes');
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.moveSelection('- 1 day');
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.moveSelection('+ 1 day');
          break;
      }
    }
  }
  private moveSelection(modifier: string|null = null): void
  {
    const selection = this.getSelection();
    const _MS_HALF_HOUR = 30 * 60 * 1000;
    const _MS_ONE_DAY = 24 * 60 * 60 * 1000;
    const _MAX_TRIES = 30;

    if(modifier && selection && selection.start && selection.end)
    {
      let newStart = new Date(selection.start);
      let newEnd = new Date(selection.end);
      let isAvailable = false;
      let msOffset = 0;

      //get move step in milliseconds 
      switch (modifier) {
        case '- 30 minutes':
          msOffset = -1 * _MS_HALF_HOUR;
          break;
        case '+ 30 minutes':
          msOffset = _MS_HALF_HOUR;
          break;
        case '- 1 day':
          msOffset = -1 * _MS_ONE_DAY;
          break;
        case '+ 1 day':
          msOffset = _MS_ONE_DAY;
          break;
      }

      //try searching for possible slot, jump over different events if needed
      if(msOffset)
      {
        for (let index = 0; index < _MAX_TRIES; index++) {
          newStart.setTime(newStart.getTime() + msOffset);
          newEnd.setTime(newEnd.getTime() + msOffset);
          if(newStart < this.reservationStart) break;
          isAvailable = this.isAvailable(newStart, newEnd);
          if(isAvailable) break;
        }
      }

      //if found in reasonable time, do move process
      if(isAvailable)
      {
        let viewCurrentStart = this.calendar?.view.currentStart
        let viewCurrentEnd = this.calendar?.view.currentEnd

        //move calendar view to keep selection visible
        while(viewCurrentStart && newStart < viewCurrentStart) {
          this.calendar?.prev();
          viewCurrentStart = this.calendar?.view.currentStart;
        }
        while(viewCurrentEnd && newEnd > viewCurrentEnd) {
          this.calendar?.next();
          viewCurrentEnd = this.calendar?.view.currentEnd;
        }

        //the actual move
        selection.setDates(newStart, newEnd);
        //focus on moved selection (is rendered again)
        this.focusOnSelection();
      }
    }
  }

  private focusOnSelection(): void
  {
    const selectionEl = this.calendarEl.querySelector('[data-event-id="selection"]') as HTMLElement|null;
    selectionEl?.focus();
  }

  public getCalendar(): Calendar
  {
    return this.createCalendar();
  }

  private destroyCalendar(): void
  {
    if(this.calendar) {
      this.calendar.destroy();
      this.calendar = null;
    }
    
    if(this.picker) {
      this.picker.remove();
      this.picker = null;
    }
  }

  private createCalendar(): Calendar
  {
    if(this.calendar) {
      return this.calendar;
    }

    this.calendar = new Calendar(this.calendarEl, {

      //intl
      locale: 'cs',
      timeZone: 'Europe/Prague',
      plugins: [ timeGridPlugin, interactionPlugin, luxonPlugin ],
      
      //business config
      validRange: {
        start: this.reservationStart,
        end: this.reservationEnd
      },
      businessHours: this.businessHours,

      //dragging, resizing, event placement
      eventOverlap: false,
      editable: true,
      //drag
      eventConstraint: 'businessHours',
      eventDurationEditable: true,
      eventResizableFromStart: true,
      eventStartEditable: true,
      //select
      selectConstraint: 'businessHours',
      selectable: true,
      selectOverlap: false,
      forceEventDuration: true,
      snapDuration: "01:00:00",
      eventInteractive: true,

      //views
      initialView: 'timeGridDay',
      allDaySlot: false,
      slotMinTime: this.slotMinTime,
      slotMaxTime: this.slotMaxTime,
      slotDuration: "00:30:00",
      dateIncrement: { days: 1 },
      slotLabelFormat: {
        hour: '2-digit',
        minute: '2-digit'
      },
      nowIndicator: true,
      contentHeight: 'auto',
      expandRows: true,
      views: {
        timeGridOneDay: {
          type: 'timeGridWeek',
          duration: { days: 1 }
        },
        timeGridTwoDays: {
          type: 'timeGridWeek',
          duration: { days: 2 }
        },
        timeGridThreeDays: {
          type: 'timeGridWeek',
          duration: { days: 3 }
        },
        timeGridFourDays: {
          type: 'timeGridWeek',
          duration: { days: 4 }
        },
        timeGridFiveDays: {
          type: 'timeGridWeek',
          duration: { days: 5 }
        },
        timeGridSixDays: {
          type: 'timeGridWeek',
          duration: { days: 6 }
        },
        timeGridWeek: {
          type: 'timeGridWeek',
          duration: { days: 7 },
        }
      },

      //controls
      longPressDelay: 20,
      headerToolbar: {
        left: 'goto',
        center: 'title',
        right: 'prev,next',
      },
      footerToolbar: {
        center: 'durationMinus durationPlus'
      },
      customButtons: {
        goto: {
          text: '',
          hint: 'Vybrat datum',
          icon: 'icon-calendar'
        },
        durationMinus: {
          text: 'Ubrat hodinu',
          hint: 'Ubrat 1 hodinu',
          click: (event, element) => {
            this.decrementDuration();
          }
        },
        durationPlus: {
          text: 'Přidat hodinu',
          hint: 'Přidat 1 hodinu',
          click: (event, element) => {
            
            if(this.calendar?.getEventById('selection')) {
              this.incrementDuration();
            } else {
              //create slot if no selection present
              const start = this.getFirstPossibleSlot(this.calendar?.view.currentStart);
              const end = new Date(start);
              end.setUTCHours(end.getUTCHours() + this.minDuration);

              this.calendar?.addEvent({
                start: start,
                end: end,
                id: 'selection'
              })
            }
          }
        },
      },

      events: (fetchInfo, successCallback, errorCallback) => {
        successCallback(this.events);
      },

      eventDidMount: (arg) => {
        //add event id to the element for possibility to find it and focus on it
        if(arg.el instanceof HTMLElement) {
          arg.el.dataset.eventId = arg.event.id;
        }
      },
      //hooks
      eventContent: (arg) => {

        if(arg.event.id == "selection") {

          const title = document.createElement('p');
          
          //format range
          if(arg.event.start && arg.event.end) {
            const formatter = new Intl.DateTimeFormat("cs-CZ", {
              hour: 'numeric',
              minute: '2-digit',
              timeZone: "Europe/Prague",
            })
            title.textContent = formatter.format(arg.event.start) + '–' + formatter.format(arg.event.end);
          }

          const duration = this.getEventDuration(arg.event);
          if(duration && duration > 1) {

            const lengthElem = document.createElement('p');
            
            switch (duration) {
              case 1:
                lengthElem.textContent = duration + ' hodina';
                break;
              case 2:
              case 3:
              case 4:
                lengthElem.textContent = duration + ' hodiny';
                break;
              default:
                lengthElem.textContent = duration + ' hodin';
                break;
            }
            return {
              domNodes: [title, lengthElem]
            }
          }

          return {
            domNodes: [title]
          }
        }
      },

      //js-events
      //event-events
      eventAdd: (arg) => {

        //remove old event first
        this.selection?.remove();

        if(arg.event.id == 'selection') {
          //everything should be ok, addEvent is happening after all checks
          this.afterEventUpdate(arg.event);
        }
      },
      eventReceive: (arg) => {

        //remove old event first
        this.selection?.remove();

        if(arg.event.id == 'selection') {
          //everything should be ok, eventRecieve should be checked correctly by fullcalendar
          this.afterEventUpdate(arg.event);
        }
      },
      eventChange: (arg) => {

        //check availability
        //force hour grid
        //force min duration
        const newEvent = arg.event;
        if(newEvent.start && newEvent.end) {
          const isAvailable = this.isAvailable(newEvent.start, newEvent.end);
          
          if(!isAvailable) {
            arg.revert();
            return false;
          }

          if(newEvent.start.getUTCMinutes() != newEvent.end.getUTCMinutes()) {

            const newEnd = new Date(newEvent.end);
            newEnd.setUTCMinutes(newEvent.start.getUTCMinutes());
            newEvent.setEnd(newEnd);
          }

          const newDuration = newEvent.end.getUTCHours() - newEvent.start.getUTCHours();
          if(newDuration < this.minDuration) {
            this.setDuration(this.minDuration);
          }
          if(newDuration > this.maxDuration) {
            this.setDuration(this.maxDuration);
          }
          //everything is ok
          //save properties, fire events
          this.afterEventUpdate(newEvent);
        }
      },
      eventRemove: (arg) => {
        if(arg.event.id == 'selection') {
          //everything is ok
          //there is nothing
          this.afterEventUpdate();
        }
      },

      //interaction events
      //click or drag
      select: (dateSelect) => {
        let duration = dateSelect.end.getUTCHours() - dateSelect.start.getUTCHours();
      
        if(duration > this.minDuration) {
          //is drag of intentional length – use that length
          this.duration = duration;
        } else {
          //is click or short drag – use automatic length
          this.duration = Math.max(this.durationPreset, duration);
        }
        this.setSelection(dateSelect.start);
      },
      eventMouseEnter: (mouseEnterInfo) => {
        //remove existing selection when dragging
        if(
          mouseEnterInfo.jsEvent.buttons == 1
          && mouseEnterInfo.event.id == 'selection'
        ) {
          mouseEnterInfo.event.remove();
        }
      },
      //on date increment / switch
      datesSet: (arg) => {
        if(this.picker) {
          /**
           * failsafe because of sentry error APOLINAR-X
           * date of datepicker is for some reason disabled sometimes
           * my suspicion is datezone
           * but it is not that important what is in the datepicker
           * it is more important to let code run
           * so we can override this
           */
          if(
            this.picker.minDate
            && this.picker.minDate > arg.start
          ) {
            this.picker.setMin(arg.start);
          }
          this.picker.setDate(arg.start, true)
        }
      },

      //environment events
      viewDidMount: (mountArg) => {

        this.recalculateView();
      },
      windowResize: (arg) => {

        this.recalculateView();
      },
      loading: (isLoading) => {
        this.setLoading(isLoading);
      }
    });

    if(this.preselectedEvent) {
      this.calendar.select(this.preselectedEvent);
    }

    this.calendar.render();

    this.picker = datepicker('.fc-goto-button', {
      startDay: 1,
      customDays: ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'],
      customMonths: ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec','Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'],
      showAllDates: true,
      disableYearOverlay: true,
      minDate: this.reservationStart,
      maxDate: this.reservationEnd,
      dateSelected: this.reservationStart,
      onSelect: (instance: DatepickerInstance, date: Date|undefined) => {
        if(date instanceof Date) {
          this.getCalendar().gotoDate(date);
        }
      }
    });

    this.updateFirstPossibleSlot();

    return this.calendar;
  }

  private isAvailable(eventStart: Date, eventEnd: Date): boolean
  {
    const existingEvents = this.getCalendar().getEvents();

    //simple for loop because of the returns
    for (let index = 0; index < existingEvents.length; index++) {
      
      const existingEvent = existingEvents[index];

      //is overlap with different event
      if(existingEvent.id != 'selection' && existingEvent.start && existingEvent.end) {
        if(
          existingEvent.start < eventEnd
          && eventStart < existingEvent.end
        ) {
          return false;
        }
      }
    }

    //is overlap with businessHours
    const allBusinessHours = this.getCalendar().getOption('businessHours');
    if(allBusinessHours instanceof Array) {
      const dayOfWeek = eventStart.getDay()
      const businessHours = allBusinessHours.find((value) => {
        const daysOfWeek = value['daysOfWeek'];
        if(daysOfWeek instanceof Array) {
          return daysOfWeek.includes(dayOfWeek);
        }
      })

      if(businessHours) {
        const openingHours = toLuxonDuration(businessHours.startTime, this.getCalendar());
        const closingHours = toLuxonDuration(businessHours.endTime, this.getCalendar());
        
        const eventDayOpeningHours = new Date(eventStart);
        eventDayOpeningHours.setHours(openingHours.hours);
        eventDayOpeningHours.setMinutes(openingHours.minutes);

        const eventDayClosingHours = new Date(eventStart);
        eventDayClosingHours.setHours(closingHours.hours);
        eventDayClosingHours.setMinutes(closingHours.minutes);

        if(
          eventStart < eventDayOpeningHours
          ||
          eventEnd > eventDayClosingHours
        ) {
          return false;
        }
      }
    }
    //all is good?
    return true;
  }

  private afterEventUpdate(event: EventApi | null = null): void
  {
    this.selection = this.getSelection();
    this.updateDuration();
    this.fireSelectEvent();
  }

  private fireSelectEvent(): void
  {
    const eventSelect = new CustomEvent('slot-select', {
      bubbles: true,
      detail: this.getSelection()
    });
    this.calendarEl.dispatchEvent(eventSelect);
  }

  private recalculateView(): void
  {
    const windowWidth = window.innerWidth;
    let targetView: string;

    if(windowWidth < 401) {
      targetView = 'timeGridOneDay';
    } else if(windowWidth < 501) {
      targetView = 'timeGridTwoDays';
    } else if(windowWidth < 601) {
      targetView = 'timeGridThreeDays';
    } else if(windowWidth < 701) {
      targetView = 'timeGridFourDays';
    } else if(windowWidth < 801) {
      targetView = 'timeGridFiveDays';
    } else if(windowWidth < 901) {
      targetView = 'timeGridSixDays';
    } else {
      targetView = 'timeGridWeek';
    }

    this.calendar?.setOption('contentHeight', Math.max(16*32, (window.innerHeight - 16*35)));
    this.calendar?.updateSize();

    if(targetView !== this.getCalendar().view.type) {
      this.getCalendar().changeView(targetView);
    }
  }

  private getDuration(): number
  {
    this.selection = this.getSelection();
    return this.getEventDuration(this.selection) ?? this.durationPreset;
  }

  private getEventDuration(event: EventApi|null = null): number|null
  {
    if(event && event.start && event.end) {
      return this.getDiffBetweenDatesInHours(event.start, event.end);
    }
    return null;
  }

  private updateDuration(): void
  {
    this.selection = this.getSelection();
    const currentSelectionDuration = this.getEventDuration(this.selection);
    if (currentSelectionDuration) {
      this.duration = currentSelectionDuration;
    }
  }

  public setDurationPreset(duration: number): void
  {
    this.durationPreset = Math.min(this.maxDuration, Math.max(this.minDuration, duration));
  }

  private getSelection(): EventApi | null
  {
    return this.calendar?.getEventById('selection') ?? null;
  }

  private setDuration(targetDurationInput: number): number
  {
    this.selection = this.getSelection();
    const targetDuration = Math.min(this.maxDuration, Math.max(this.minDuration, targetDurationInput));
    const currentSelectionDuration = this.getEventDuration(this.selection);

    if(
      currentSelectionDuration 
      && currentSelectionDuration != targetDuration
      && this.selection //are you happy now?
      && this.selection.start //are you happy now?
    ) {
      //edit current selection and come back here to recalc

      this.setSelection(this.selection.start, targetDuration);
    }
    return this.duration;
  }

  private setSelection(start: Date, durationInput: number|null = null): void
  {
    // this.selection?.remove();

    let isAvailable: boolean = false; //failsafe
    let durationHours = durationInput ? durationInput : this.duration;


    durationHours = Math.min(this.maxDuration, Math.max(this.minDuration, durationHours));


    const durationHalfHours = durationHours * 2;

    //ceil to half-hour
    start.setMinutes(Math.ceil(start.getMinutes() / 30) * 30)
    start.setSeconds(0);
    start.setMilliseconds(0)

    const end = new Date(start);
    let endHalfHourOffset = 0;

    //incrementally offset end time by half hour
    for (;endHalfHourOffset < durationHalfHours; endHalfHourOffset++) {
      
      const durationToTry = durationHalfHours - endHalfHourOffset;
      end.setTime(start.getTime() + (durationToTry * 30 * 60 * 1000));

      //until we find available slot
      isAvailable = this.isAvailable(start, end);
      if(isAvailable) {
        break;
      } 
    }

    //incrementally offset start time by whole hour
    for (let startOffset = 0; startOffset <= endHalfHourOffset; startOffset += 2) {

      const durationToTry = durationHalfHours - startOffset;
      start.setTime(end.getTime() - (durationToTry * 30 * 60 * 1000));

      //until we find available slot
      isAvailable = this.isAvailable(start, end);
      if(isAvailable) {
        break;
      }
    }

    const resultDuration = this.getDiffBetweenDatesInHours(start, end);

    //create if selection is possible
    if(
      isAvailable
      && resultDuration >= this.minDuration
    ) {

      this.getCalendar().addEvent({
        id: 'selection',
        start: start,
        end: end
      });
    }
  }

  private getDiffBetweenDatesInHours(start: Date, end: Date): number
  {
    const _MS_PER_HOUR = 1000 * 60 * 60;
    return Math.floor((end.getTime() - start.getTime()) / _MS_PER_HOUR);
  }

  public setReviewData(reviewData: ReviewData | null): void
  {
    this.reviewData = reviewData;
  }

  private setLoading(isLoading: boolean = false): void
  {
    this.calendarEl.classList.toggle('is-loading', isLoading);
  }

  public setMinDuration(minDuration: number): number
  {
    //never less than hour
    let newMinDuration = Math.max(1, minDuration);

    //remove existing event if new minduration is shorter
    if(newMinDuration < this.minDuration) {
      this.selection?.remove(); 
    }

    this.minDuration = newMinDuration;

    //update preset with new minduration
    this.setDurationPreset(this.durationPreset);
    this.updateFirstPossibleSlot();

    return this.minDuration;
  }

  public setMaxDuration(maxDuration: number): number
  {
    if(maxDuration < this.minDuration) {
      throw new Error("Max duration is smaller than min duration");
    }
    //never more than whole day
    let newMaxDuration = Math.min(24, maxDuration);

    //remove existing event if new minduration is shorter
    if(newMaxDuration < this.maxDuration) {
      this.selection?.remove(); 
    }

    this.maxDuration = newMaxDuration;

    //update preset with new maxduration //TODO
    this.setDurationPreset(this.durationPreset);
    this.updateFirstPossibleSlot();

    return this.maxDuration;
  }

  public setBusinessHours(businessHours: BusinessHoursInput): void
  {
    this.businessHours = businessHours;
  }

  public setSlotMinTime(slotMinTime: DurationInput): void
  {
    this.slotMinTime = slotMinTime;
  }

  public setSlotMaxTime(slotMaxTime: DurationInput): void
  {
    this.slotMaxTime = slotMaxTime;
  }

  private decrementDuration(): void
  {
    const newDuration = this.getDuration() - 1;
    if(newDuration >= this.minDuration) {
      this.setDuration(newDuration);
      this.setDurationPreset(newDuration);
    }
  }

  private incrementDuration(): void
  {
    const newDuration = this.getDuration() + 1;
    if(newDuration <= this.maxDuration) {
      this.setDuration(newDuration);
      this.setDurationPreset(newDuration);
    }
  }
  
  private updateFirstPossibleSlot(): void
  {
    if(this.calendar) {
      const firstPossibleSlot = this.getFirstPossibleSlot();
      /**
       * update picker first – its date is updated by calendar change on init
       * and throws error if picker min is earlier?
       * i dont understand why
       */
      this.picker?.setMin(firstPossibleSlot); 
      this.calendar?.setOption('validRange', {start: firstPossibleSlot});
    }
  }

  private getFirstPossibleSlot(searchStart: Date|null = null): Date
  {
    let firstPossibleSlot: Date|null = null;
    if(this.calendar) {
      
      let possibleStart = searchStart ?? this.reservationStart;
      let possibleEnd = new Date(possibleStart);
      possibleEnd.setTime(possibleEnd.getTime() + this.minDuration * (60*60*1000));
  
      for (let index = 0; index < 2000; index++) {
        if(this.isAvailable(possibleStart, possibleEnd)) {
          firstPossibleSlot = possibleStart;  
          break;
        }
        possibleStart.setTime(possibleStart.getTime() + (30 * 60 * 1000));
        possibleEnd.setTime(possibleEnd.getTime() + (30 * 60 * 1000));
      }
    }
    return firstPossibleSlot ?? this.reservationStart;
  }

  public setEvents(events: EventInput[]): void
  {
    this.events = events;

    this.events.unshift({
      start: this.today,
      end: this.reservationStart,
      display: 'background'
    });

    this.calendar?.refetchEvents();

    this.updateFirstPossibleSlot();
  }
}